import type {
  AiMenuImportProgress,
  AiTurnAttachment,
  AiTurnOutcome,
} from '@restaurant/api-contracts';

export interface DockTurnAuthor {
  userId: string;
  name: string | null;
}

export interface DockTurn<TChange> {
  id: string;
  author: DockTurnAuthor | null;
  createdAt: string;
  text: string;
  attachments: AiTurnAttachment[];
  reply: string | null;
  changes: TChange[];
  outcome: AiTurnOutcome | null;
  canUndo: boolean;
  verification: { appliedCount: number; missingCount: number } | null;
}

export interface DockSession<TChange> {
  id: string;
  turns: DockTurn<TChange>[];
  hasMore: boolean;
  draft: { turnId: string; changes: TChange[] } | null;
  busy: boolean;
  menuImport: AiMenuImportProgress | null;
}

export function mergeTurnPages<T extends { id: string }>(pages: { turns: T[] }[]): T[] {
  const seen = new Set<string>();
  const merged: T[] = [];
  for (const page of [...pages].reverse()) {
    for (const turn of page.turns) {
      if (seen.has(turn.id)) continue;
      seen.add(turn.id);
      merged.push(turn);
    }
  }
  return merged;
}

export type HistoryItem<T> =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'author'; key: string; name: string | null }
  | { kind: 'turn'; key: string; turn: T };

export function historyItems<
  T extends { id: string; createdAt: string; author: DockTurnAuthor | null },
>(turns: T[], dayLabel: (iso: string) => string): HistoryItem<T>[] {
  const items: HistoryItem<T>[] = [];
  let day: string | null = null;
  let author: string | null = null;
  for (const turn of turns) {
    const label = dayLabel(turn.createdAt);
    if (label !== day) {
      day = label;
      author = null;
      items.push({ kind: 'day', key: `day:${turn.id}`, label });
    }
    const who = turn.author?.userId ?? null;
    if (who !== null && who !== author) {
      items.push({ kind: 'author', key: `author:${turn.id}`, name: turn.author?.name ?? null });
    }
    author = who;
    items.push({ kind: 'turn', key: turn.id, turn });
  }
  return items;
}

export function lastReplyOf(turns: { reply: string | null }[]): string {
  for (let i = turns.length - 1; i >= 0; i--) {
    const reply = turns[i].reply;
    if (reply) return reply;
  }
  return '';
}

export type DockPhase = 'reading' | 'streaming' | 'locked' | 'review' | 'idle';

export function dockPhase(state: {
  importStatus: AiMenuImportProgress['status'] | null;
  streaming: boolean;
  sessionBusy: boolean;
  hasDraft: boolean;
}): DockPhase {
  if (state.importStatus === 'reading') return 'reading';
  if (state.streaming) return 'streaming';
  if (state.sessionBusy) return 'locked';
  if (state.hasDraft) return 'review';
  return 'idle';
}

export const inputLocked = (phase: DockPhase): boolean =>
  phase === 'reading' || phase === 'streaming' || phase === 'locked';

export function readingPage(progress: AiMenuImportProgress): number {
  return Math.min(progress.pagesDone + 1, Math.max(progress.pagesTotal, 1));
}

export function readingRatio(progress: AiMenuImportProgress): number {
  if (progress.pagesTotal <= 0) return 0;
  return Math.min(progress.pagesDone / progress.pagesTotal, 1);
}

export function sourceLabels(
  sources: { attachmentId: string; page: number; label: string }[],
): Map<string, string[]> {
  const labels = new Map<string, string[]>();
  for (const source of [...sources].sort((a, b) => a.page - b.page)) {
    labels.set(source.attachmentId, [...(labels.get(source.attachmentId) ?? []), source.label]);
  }
  return labels;
}
